const WARSAW_TIMEZONE = "Europe/Warsaw";
const PL_LOCALE = "pl-PL";

interface LocalDateTime {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
}

/**
 * Converts a UTC date string to an object with date/time parts in Europe/Warsaw timezone.
 * @returns {{ year, month, day, hour, minute, second }} or null
 */
export const convertToLocalDateTime = (
  utcDateString: string,
  timeZone: string = WARSAW_TIMEZONE
): LocalDateTime | null => {
  if (!utcDateString) return null;
  const date = new Date(utcDateString);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)!.value;
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour"),
    minute: get("minute"),
    second: get("second"),
  };
};

const dateTimeFormatter = new Intl.DateTimeFormat(PL_LOCALE, {
  dateStyle: "short",
  timeStyle: "short",
  timeZone: WARSAW_TIMEZONE,
});

/**
 * Formats an ISO date string as a short date with time in Europe/Warsaw timezone (pl-PL locale).
 * @returns formatted string or placeholder if input is null
 */
export const formatToLocalDateTime = (
  isoString: string | null,
  placeholder = "—"
): string => {
  if (!isoString) return placeholder;
  return dateTimeFormatter.format(new Date(isoString));
};

const dateFormatter = new Intl.DateTimeFormat(PL_LOCALE, {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: WARSAW_TIMEZONE,
});

/**
 * Formats an ISO date string as DD.MM.YYYY in Europe/Warsaw timezone (pl-PL locale).
 * @returns formatted string or placeholder if input is null
 */
export const formatToLocalDate = (
  isoString: string | null,
  placeholder = "—"
): string => {
  if (!isoString) return placeholder;
  const parts = dateFormatter.formatToParts(new Date(isoString));
  const day = parts.find((p) => p.type === "day")!.value;
  const month = parts.find((p) => p.type === "month")!.value;
  const year = parts.find((p) => p.type === "year")!.value;
  return `${day}.${month}.${year}`;
};

const monthYearFormatter = new Intl.DateTimeFormat(PL_LOCALE, {
  month: "2-digit",
  year: "numeric",
  timeZone: WARSAW_TIMEZONE,
});

/**
 * Formats an ISO date string as MM.YYYY in Europe/Warsaw timezone.
 * @returns formatted string or placeholder if input is null
 */
export const formatToLocalMonthYear = (
  isoString: string | null,
  placeholder = "—"
): string => {
  if (!isoString) return placeholder;
  const parts = monthYearFormatter.formatToParts(new Date(isoString));
  const month = parts.find((p) => p.type === "month")!.value;
  const year = parts.find((p) => p.type === "year")!.value;
  return `${month}.${year}`;
};
